import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { getAreas } from '../../../lib/api';
import styled from 'styled-components';
import { GetStaticPaths, GetStaticPropsContext } from 'next';
import Image from 'next/image';
import { getFilterOptions, getPropertyTypes } from '../../../new-lib/apis';
import SellPropertyForm from '../../../features/contact/components/sell-property/SellPropertyForm';
import { useSelector } from 'react-redux';
import { wrapper } from '../../../redux/store';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';

export default function SellMyProperty(props: any) {
  const deliveryDates = JSON.parse(props.filterOptions).delivery_date;
  const { t } = useTranslation('sell-my-property');
  const isMobile = useSelector((state: any) => state.app.isMobile);

  return (
    <>
      <Container>
        <div className="header">
          {isMobile ? (
            <Image
              priority
              src={'/assets/icons/sell-my-property/house-for-sale.svg'}
              alt="sell-your-home"
              width={87}
              height={82}
            />
          ) : (
            <Image
              priority
              src={'/assets/icons/sell-my-property/house-for-sale-color.svg'}
              alt="sell-your-home"
              width={134}
              height={142}
            />
          )}
          <h1>{t('sell_your_property')}</h1>
        </div>
        <div className="steps-card-container">
          <StepCard>
            <div className="step-number">
              <span>1</span>
              <p>{t('step')}</p>
            </div>
            <div className="step-text">
              <span className="step-title">{t('list_your_property')}</span>
              <p>{t('list_your_property_description')}</p>
            </div>
          </StepCard>
          <StepCard>
            <div className="step-number">
              <span>2</span>
              <p>{t('step')}</p>
            </div>
            <div className="step-text">
              <span className="step-title">{t('agents_call')}</span>
              <p>{t('agents_call_description')}</p>
            </div>
          </StepCard>
          <StepCard>
            <div className="step-number">
              <span>3</span>
              <p>{t('step')}</p>
            </div>
            <div className="step-text">
              <span className="step-title">{t('meet_buyers')}</span>
              <p>{t('meet_buyers_description')}</p>
            </div>
          </StepCard>
        </div>
        <div className="form-container">
          <SellPropertyForm
            areas={props.areas.map((area: any) => ({
              value: area.id,
              label: area.name
            }))}
            propertyTypes={props.propertyTypes.map((propertyType: any) => ({
              value: propertyType.id,
              label: propertyType.name
            }))}
            deliveryDates={deliveryDates}
          />
        </div>
      </Container>
    </>
  );
}

const Container = styled.div`
  width: 95%;
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 64px;
  .header {
    margin-top: 64px;
    display: flex;
    flex-direction: column;
    align-items: center;
    order: 1;

    h1 {
      font-family: materialBold;
      font-size: 30px;
      margin-top: 16px;
    }
  }

  .steps-card-container {
    display: flex;
    gap: 16px;
    justify-content: center;
    order: 2;
  }

  .form-container {
    order: 3;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.max}) {
    gap: 16px;

    .header {
      margin-top: 6px;

      h1 {
        font-size: 18px;
      }
    }

    .steps-card-container {
      flex-direction: column;
      align-items: center;
      order: 3;
      margin-top: 0px;
      margin-bottom: 16px;
    }

    .form-container {
      order: 2;
    }
  }
`;

const StepCard = styled.div`
  width: 300px;
  height: 182px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background-color: ${props => props.theme.light.colors.whitish};
  border-radius: 10px;

  .step-text {
    text-align: center;

    .step-title {
      font-family: materialBold;
      font-size: 16px;
      color: ${props => props.theme.light.colors.black};
    }

    p {
      font-size: 14px;
      color: ${props => props.theme.light.colors.black};
      text-transform: capitalize;
      text-align: center;
      margin: 6px 16px;
    }
  }

  .step-number {
    margin-top: 16px;
    min-height: 70px;
    height: 70px;
    max-height: 70px;
    min-width: 70px;
    width: 70px;
    max-width: 70px;
    background-color: ${props => props.theme.light.colors.white};
    border-radius: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    span {
      font-family: materialBold;
      font-size: 22px;
    }

    p {
      margin: 0px;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.max}) {
    flex-direction: row;
    margin: 0px 16px;
    width: 100%;
    min-width: 100px;
    height: auto;
    //justify-content: baseline;
    gap: 0px;

    .step-text {
      width: 100%;
      height: auto;
      padding: 16px 0px;
      text-align: start;

      .step-title,
      p {
        text-align: start;
        margin: 6px 0px;
        width: calc(100% - 20px);
      }
    }

    .step-number {
      margin: 16px;
    }

    p {
      margin: 0px;
      width: auto;
    }
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [areas, propertyTypes, filterOptions] = await Promise.all([
        getAreas(locale),
        getPropertyTypes(locale),
        getFilterOptions(locale)
      ]);

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        revalidate: 60 * 60 * 24,
        props: {
          areas,
          propertyTypes,
          ...(await serverSideTranslations(locale as string, [
            'common',
            'contact',
            'sell-my-property',
            'search'
          ])),
          filterOptions: JSON.stringify(filterOptions) || '',
          layout: { footerLinks: [] }
        }
      };
    }
);

export const getStaticPaths = (async () => {
  return { paths: StaticPagesUtility.get(), fallback: false };
}) satisfies GetStaticPaths;
